import React from 'react';
import { LegalPageLayout } from '@/components/shared/LegalPageLayout';

export default function GdprPage() {
  return (
    <LegalPageLayout title="GDPR Compliance" lastUpdated="January 15, 2025">
      <div className="space-y-8">
        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">1. Our Commitment to GDPR</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            Agentick is committed to complying with the General Data Protection Regulation (GDPR) for all users, including those in the European Economic Area (EEA). We process personal data lawfully, fairly, and transparently, and we implement appropriate technical and organizational measures to protect your data.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">2. Legal Basis for Processing</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed mb-3">
            We process your personal data based on the following legal grounds:
          </p>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground font-body ml-2">
            <li><span className="text-foreground font-medium">Contractual necessity</span> — Processing required to provide our services and fulfill our agreement with you</li>
            <li><span className="text-foreground font-medium">Legitimate interest</span> — Processing for analytics, service improvement, and security, where balanced against your rights</li>
            <li><span className="text-foreground font-medium">Consent</span> — Processing based on your explicit opt-in, such as marketing communications</li>
            <li><span className="text-foreground font-medium">Legal obligation</span> — Processing required to comply with applicable laws and regulations</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">3. Your Rights Under GDPR</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed mb-3">
            As a data subject, you have the following rights:
          </p>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground font-body ml-2">
            <li><span className="text-foreground font-medium">Right of Access</span> — Request a copy of all personal data we hold about you</li>
            <li><span className="text-foreground font-medium">Right to Rectification</span> — Request correction of inaccurate or incomplete personal data</li>
            <li><span className="text-foreground font-medium">Right to Erasure</span> — Request deletion of your personal data ("right to be forgotten")</li>
            <li><span className="text-foreground font-medium">Right to Restrict Processing</span> — Request limitation of how we process your data</li>
            <li><span className="text-foreground font-medium">Right to Data Portability</span> — Receive your data in a structured, machine-readable format</li>
            <li><span className="text-foreground font-medium">Right to Object</span> — Object to processing based on legitimate interest or direct marketing</li>
            <li><span className="text-foreground font-medium">Right Not to Be Subject to Automated Decision-Making</span> — Object to decisions made solely by automated processing</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">4. Data Processing & Storage</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            Your data is primarily processed and stored on servers located in India. Where data is transferred outside the EEA, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission. All data transfers comply with GDPR Chapter V requirements.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">5. Data Protection Measures</h2>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground font-body ml-2">
            <li>Encryption of data in transit (TLS 1.3) and at rest (AES-256)</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Role-based access controls with principle of least privilege</li>
            <li>Data processing agreements with all sub-processors</li>
            <li>Privacy impact assessments for new features and data flows</li>
            <li>Employee training on data protection and GDPR compliance</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">6. Data Breach Notification</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            In the event of a personal data breach, we will notify the relevant supervisory authority within 72 hours of becoming aware of the breach, where required by GDPR. If the breach is likely to result in high risk to your rights and freedoms, we will also notify you directly without undue delay.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">7. Data Protection Officer</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            For GDPR-related inquiries or to exercise your data rights, please contact our Data Protection Officer at <span className="text-primary">dpo@agentick.com</span>. We will respond to all legitimate requests within 30 days.
          </p>
        </section>
      </div>
    </LegalPageLayout>
  );
}
